import { Suspense, lazy } from "react";
import { Route, Link, Routes, Navigate } from "react-router-dom";

import * as H from "history";

import { isInRole, isLogged } from "../../helpers/auth";
import { useSelector } from "react-redux";
import { Facebook } from "react-spinners-css";

import Home from "../Dashboard/Home";
import Configuration from "../Dashboard/Configuration";

import Users from "../Dashboard/User";
import UserCreate from "../Dashboard/User/Create";
import UserEdit from "../Dashboard/User/Edit";
import UserDetail from "../Dashboard/User/Detail";

import BankBillets from "../Dashboard/BankBillet";
import BankBilletCreate from "../Dashboard/BankBillet/Create";
import BankBilletEdit from "../Dashboard/BankBillet/Edit";
import BankBilletDetail from "../Dashboard/BankBillet/Detail";

import Meis from "../Dashboard/Mei";
import MeiCreate from "../Dashboard/Mei/Create";
import MeiEdit from "../Dashboard/Mei/Edit";
import MeiDetail from "../Dashboard/Mei/Detail";

import Companies from "../Dashboard/Company";
import CompanyCreate from "../Dashboard/Company/Create";
import CompanyEdit from "../Dashboard/Company/Edit";
import CompanyDetail from "../Dashboard/Company/Detail";

import Invoices from "../Dashboard/Invoices";

import Dases from "../Dashboard/Das";
import DasCreate from "../Dashboard/Das/Create";
import DasEdit from "../Dashboard/Das/Edit";
import DasDetail from "../Dashboard/Das/Detail";

import Subscriptions from "../Dashboard/Subscriptions";
import SubscriptionCreate from "../Dashboard/Subscriptions/Create";
import SubscriptionEdit from "../Dashboard/Subscriptions/Edit";
import SubscriptionDetail from "../Dashboard/Subscriptions/Detail";
import SubscriptionPay from "../Dashboard/Subscriptions/Pay";
import MeiOfLogisticOperator from "../Dashboard/MeiOfLogisticOperator";

import Marketings from "../Dashboard/Marketing";
import MarketingCreate from "../Dashboard/Marketing/Create";
import MarketingEdit from "../Dashboard/Marketing/Edit";
import MarketingDetail from "../Dashboard/Marketing/Detail";

import NFSes from "../Dashboard/NFSe";
import NFSeCreate from "../Dashboard/NFSe/Create";
import NFSeEdit from "../Dashboard/NFSe/Edit";
import NFSeDetail from "../Dashboard/NFSe/Detail";

import Transfers from "../Dashboard/Transfer";
import TransferCreate from "../Dashboard/Transfer/Create";
import TransferEdit from "../Dashboard/Transfer/Edit";
import TransferDetail from "../Dashboard/Transfer/Detail";

import CreateMei from "../Dashboard/CreateMei";

import Invoicing from "views/Dashboard/Invoicing";

import Dasn from "views/Dashboard/Dasn";

import Emails from "views/Dashboard/Emails";

const Dashboard = lazy(() => import("../Dashboard"));
const Login = lazy(() => import("../Login"));
const Hub = lazy(() => import("../Hub"));
const RecoverPassword = lazy(() => import("../RecoverPassword"));

const ContractForm = lazy(() => import("../ContractForm"));

type AuthRouteProps = {
	children: JSX.Element;
	user: any;
	needRoles: string[];
};

export const AuthRoute: React.FC<AuthRouteProps> = ({
	user,
	needRoles,
	children,
	...rest
}) => {
	if (isInRole(user, needRoles)) return children;

	return null;
};

function AppRouter() {
	const login = useSelector((s: any) => s.login);

	const loggedIn = isLogged(login);

	const { user } = login;

	const isAdmin = isInRole(user, ["admin"]);

	return (
		<Suspense
			fallback={
				<div
					style={{
						height: "100vh",
						width: "100vw",
						display: "flex",
						alignItems: "center",
						justifyContent: "center"
					}}
				>
					<Facebook color="#97854f" />
				</div>
			}
		>
			<Routes>
				<Route
					path="/Dashboard"
					element={loggedIn ? <Dashboard /> : <Navigate to="/Login" />}
				>
					<Route
						index
						element={
							<AuthRoute user={user} needRoles={["admin", "user", "logisticOperator"]}>
								<Home />
							</AuthRoute>
						}
					/>
					<Route
						path="Configuration"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<Configuration />
							</AuthRoute>
						}
					/>

					<Route
						path="User/CreateMei"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<CreateMei />
							</AuthRoute>
						}
					/>

					<Route
						path="User"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<Users />
							</AuthRoute>
						}
					/>

					<Route
						path="User/Create"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<UserCreate />
							</AuthRoute>
						}
					/>

					<Route
						path="User/:id"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<UserDetail />
							</AuthRoute>
						}
					/>

					<Route
						path="User/:id/Edit"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<UserEdit />
							</AuthRoute>
						}
					/>

					<Route
						path="BankBillet"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<BankBillets />
							</AuthRoute>
						}
					/>

					<Route
						path="BankBillet/Create"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<BankBilletCreate />
							</AuthRoute>
						}
					/>

					<Route
						path="BankBillet/:id/Edit"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<BankBilletEdit />
							</AuthRoute>
						}
					/>

					<Route
						path="BankBillet/:id"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<BankBilletDetail />
							</AuthRoute>
						}
					/>

					<Route
						path="Company"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<Companies />
							</AuthRoute>
						}
					/>

					<Route
						path="Company/Create"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<CompanyCreate />
							</AuthRoute>
						}
					/>

					<Route
						path="Company/:id/Edit"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<CompanyEdit />
							</AuthRoute>
						}
					/>

					<Route
						path="Company/:id"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<CompanyDetail />
							</AuthRoute>
						}
					/>

					<Route
						path="Mei"
						element={
							<AuthRoute user={user} needRoles={["admin", "logisticOperator"]}>
								<Meis />
							</AuthRoute>
						}
					/>

					<Route
						path="Mei/Create"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<MeiCreate />
							</AuthRoute>
						}
					/>

					<Route
						path="Mei/:id/Edit"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<MeiEdit />
							</AuthRoute>
						}
					/>

					<Route
						path="Mei/:id"
						element={
							<AuthRoute user={user} needRoles={["admin", "logisticOperator"]}>
								<MeiDetail />
							</AuthRoute>
						}
					/>

					<Route
						path="NFSe"
						element={
							<AuthRoute user={user} needRoles={["admin", "logisticOperator"]}>
								<NFSes />
							</AuthRoute>
						}
					/>

					<Route
						path="NFSe/Create"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<NFSeCreate />
							</AuthRoute>
						}
					/>

					<Route
						path="NFSe/:id/Edit"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<NFSeEdit />
							</AuthRoute>
						}
					/>

					<Route
						path="NFSe/:id"
						element={
							<AuthRoute user={user} needRoles={["admin", "logisticOperator"]}>
								<NFSeDetail />
							</AuthRoute>
						}
					/>

					<Route
						path="Invoice"
						element={
							<AuthRoute user={user} needRoles={["admin", "logisticOperator"]}>
								<Invoices />
							</AuthRoute>
						}
					/>

					<Route
						path="Invoicing"
						element={
							<AuthRoute user={user} needRoles={["admin", "logisticOperator"]}>
								<Invoicing />
							</AuthRoute>
						}
					/>

					<Route
						path="Das"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<Dases />
							</AuthRoute>
						}
					/>

					<Route
						path="Das/Create"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<DasCreate />
							</AuthRoute>
						}
					/>

					<Route
						path="Das/:id/Edit"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<DasEdit />
							</AuthRoute>
						}
					/>

					<Route
						path="Das/:id"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<DasDetail />
							</AuthRoute>
						}
					/>

					<Route
						path="Dasn"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<Dasn />
							</AuthRoute>
						}
					/>

					<Route
						path="Dasn/Create"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<DasCreate />
							</AuthRoute>
						}
					/>

					<Route
						path="Dasn/:id/Edit"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<DasEdit />
							</AuthRoute>
						}
					/>

					<Route
						path="Dasn/:id"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<DasDetail />
							</AuthRoute>
						}
					/>

					<Route
						path="Transfer"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<Transfers />
							</AuthRoute>
						}
					/>

					<Route
						path="Transfer/Create"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<TransferCreate />
							</AuthRoute>
						}
					/>

					<Route
						path="Transfer/:id/Edit"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<TransferEdit />
							</AuthRoute>
						}
					/>

					<Route
						path="Transfer/:id"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<TransferDetail />
							</AuthRoute>
						}
					/>

					<Route
						path="Marketing"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<Marketings />
							</AuthRoute>
						}
					/>

					<Route
						path="Marketing/Create"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<MarketingCreate />
							</AuthRoute>
						}
					/>

					<Route
						path="Marketing/:id/Edit"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<MarketingEdit />
							</AuthRoute>
						}
					/>

					<Route
						path="Marketing/:id"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<MarketingDetail />
							</AuthRoute>
						}
					/>

					<Route
						path="Subscriptions"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<Subscriptions />
							</AuthRoute>
						}
					/>
					<Route
						path="Subscriptions/Pay"
						element={
							<AuthRoute user={user} needRoles={["logisticOperator", "admin"]}>
								<SubscriptionPay />
							</AuthRoute>
						}
					/>

					<Route
						path="LogisticOperator/Mei"
						element={
							<AuthRoute user={user} needRoles={["logisticOperator", "admin"]}>
								<MeiOfLogisticOperator />
							</AuthRoute>
						}
					/>

					<Route
						path="Subscriptions/Create"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<SubscriptionCreate />
							</AuthRoute>
						}
					/>

					<Route
						path="Subscriptions/:id/Edit"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<SubscriptionEdit />
							</AuthRoute>
						}
					/>

					<Route
						path="Subscriptions/:id"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<SubscriptionDetail />
							</AuthRoute>
						}
					/>

					<Route
						path="Email"
						element={
							<AuthRoute user={user} needRoles={["admin"]}>
								<Emails />
							</AuthRoute>
						}
					/>

					<Route
						path="*"
						element={
							<Navigate
								to={isAdmin ? "/Dashboard" : "/Dashboard/Subscriptions/Pay"}
								replace
							/>
						}
					/>
				</Route>
				<Route
					path="/Login"
					element={!loggedIn ? <Login /> : <Navigate to="/Dashboard" replace />}
				/>
				<Route
					path="/RecoverPassword"
					element={loggedIn ? <Navigate to="/" /> : <RecoverPassword />}
				/>

				<Route path="/contrato/form" element={<ContractForm />} />
				{/* <Route exact path="/" component={Hub} /> */}

				<Route
					path={"*"}
					element={
						<Navigate to={loggedIn ? "/Dashboard/NFSe" : "/login"} replace />
					}
				/>
			</Routes>
		</Suspense>
	);
}

export default AppRouter;
